import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { Search } from "lucide-react";
import { ShellButton } from "@/components/layout/ShellControl";

interface CommandSearchProps {
  mobile?: boolean;
}

const CommandSearchDialog = lazy(() =>
  import("@/components/layout/CommandSearchDialog").then((module) => ({
    default: module.CommandSearchDialog,
  })),
);

export function CommandSearch({ mobile = false }: CommandSearchProps) {
  const [open, setOpen] = useState(false);
  const [hasOpened, setHasOpened] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const openSearch = () => {
    setHasOpened(true);
    setOpen(true);
  };

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (!nextOpen) {
      window.requestAnimationFrame(() => triggerRef.current?.focus());
    }
  };

  useEffect(() => {
    if (mobile) return;
    const handleShortcut = (event: KeyboardEvent) => {
      if (event.key === "k" && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        setHasOpened(true);
        setOpen((current) => !current);
      }
    };
    document.addEventListener("keydown", handleShortcut);
    return () => document.removeEventListener("keydown", handleShortcut);
  }, [mobile]);

  return (
    <>
      <ShellButton
        ref={triggerRef}
        variant={mobile ? "ghost" : "outline"}
        size={mobile ? "icon" : "sm"}
        className={mobile ? "" : "inline-flex gap-2 text-muted-foreground"}
        onClick={openSearch}
        aria-label={mobile ? "Search GeoMiner" : undefined}
      >
        <Search className="h-4 w-4" />
        {!mobile && (
          <>
            <span className="hidden xl:inline text-sm">Search…</span>
            <kbd className="pointer-events-none ml-1 hidden 2xl:inline-flex h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground">
              Ctrl/⌘ K
            </kbd>
          </>
        )}
      </ShellButton>

      {hasOpened && (
        <Suspense fallback={null}>
          <CommandSearchDialog open={open} onOpenChange={handleOpenChange} />
        </Suspense>
      )}
    </>
  );
}
